/**
 * Ctrl+K global search overlay.
 * Defaults to /api/platform/search; modules can override searchPath (e.g. HR → /api/hr/search).
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { EmptyState, ErrorState, Skeleton } from "./States";

function joinUrl(base, path) {
  const b = String(base || "").replace(/\/+$/, "");
  const p = String(path || "").startsWith("/") ? path : `/${path || ""}`;
  return `${b}${p}`;
}

export function GlobalSearchModal({
  open,
  onClose,
  apiBase,
  getToken,
  searchPath = "/api/platform/search",
  placeholder = "Search customers, work orders, invoices, quotations, assets…",
}) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    if (!open) {
      setQ("");
      setGroups([]);
      setErr("");
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (q.trim().length < 2) {
      setGroups([]);
      setErr("");
      return;
    }
    const t = setTimeout(async () => {
      setBusy(true);
      setErr("");
      try {
        const token = typeof getToken === "function" ? getToken() : "";
        if (!token) {
          throw new Error("Not signed in — refresh the page and log in again.");
        }
        if (!apiBase) {
          throw new Error("Search API base URL is not configured.");
        }
        const res = await fetch(
          `${joinUrl(apiBase, searchPath)}?q=${encodeURIComponent(q.trim())}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "X-Client": "web",
              Accept: "application/json",
            },
          }
        );
        const text = await res.text();
        let body = {};
        try {
          body = text ? JSON.parse(text) : {};
        } catch {
          throw new Error(res.ok ? "Invalid search response" : `HTTP ${res.status}`);
        }
        if (!res.ok) {
          throw new Error(body.message || body.error || `HTTP ${res.status}`);
        }
        const next = body.data?.groups || body.groups || [];
        setGroups(Array.isArray(next) ? next : []);
      } catch (e) {
        setErr(e.message || "Search failed");
        setGroups([]);
      } finally {
        setBusy(false);
      }
    }, 280);
    return () => clearTimeout(t);
  }, [q, open, apiBase, getToken, searchPath]);

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onKeyDown]);

  if (!open) return null;

  return (
    <div
      className="crane-search-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="crane-search-modal">
        <input
          className="crane-search-modal__input"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={placeholder}
          aria-label="Search query"
        />
        <div className="crane-search-modal__body">
          {busy && <Skeleton rows={4} />}
          {!busy && err && <ErrorState title="Search failed" description={err} />}
          {!busy && !err && q.trim().length >= 2 && groups.length === 0 && (
            <EmptyState title="No results" description={`Nothing matched “${q.trim()}”.`} />
          )}
          {!busy &&
            !err &&
            groups.map((g) => (
              <div key={g.type || g.key || g.label} style={{ marginBottom: 12 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: "uppercase",
                    color: "var(--crane-on-surface-variant)",
                    padding: "4px 12px",
                  }}
                >
                  {g.label || g.key}
                </div>
                {(g.items || []).map((item) =>
                  item.href ? (
                    <Link
                      key={`${g.type || g.key}-${item.id}`}
                      to={item.href}
                      className="crane-search-modal__item"
                      onClick={onClose}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</div>
                        {item.subtitle && (
                          <div style={{ color: "var(--crane-on-surface-variant)", fontSize: 12 }}>
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                      <span style={{ fontSize: 12, color: "var(--crane-primary)" }}>Open</span>
                    </Link>
                  ) : (
                    <div key={`${g.type || g.key}-${item.id}`} className="crane-search-modal__item">
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{item.title}</div>
                        {item.subtitle && (
                          <div style={{ color: "var(--crane-on-surface-variant)", fontSize: 12 }}>
                            {item.subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                )}
              </div>
            ))}
          {q.trim().length < 2 && !busy && !err && (
            <EmptyState title="Start typing" description="Enter at least 2 characters. Press Esc to close." />
          )}
        </div>
      </div>
    </div>
  );
}

export default GlobalSearchModal;
